import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Lightning, User, Barbell, EnvelopeSimple, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";

const checkPolicy = (pwd) => ({
  length: pwd.length >= 8,
  upper: /[A-Z]/.test(pwd),
  digit: /\d/.test(pwd),
  special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(pwd),
});

const MAX_AVATAR_BYTES = 800 * 1024;
const readFileAsDataURL = (file) => new Promise((res, rej) => {
  const fr = new FileReader();
  fr.onload = () => res(fr.result);
  fr.onerror = rej;
  fr.readAsDataURL(file);
});

export default function Register() {
  const { register, user, ready, formatApiError } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // If the user is already logged in, adding a second role does NOT require
  // re-entering their email/password. Redirect them to the dedicated flow.
  React.useEffect(() => {
    if (ready && user) {
      const roles = user.roles || (user.role ? [user.role] : []);
      if (roles.includes("instructor")) {
        navigate("/instructor", { replace: true });
      } else {
        navigate("/become-instructor", { replace: true });
      }
    }
  }, [ready, user, navigate]);

  const [role, setRole] = useState("client");
  const [form, setForm] = useState({
    email: "", password: "", password_confirm: "", name: "", phone: "", bio: "",
    address: "", date_of_birth: "", avatar_url: "", attestation_accepted: false,
  });
  const [avatarPreview, setAvatarPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState(null);

  const policy = useMemo(() => checkPolicy(form.password), [form.password]);
  const policyOk = policy.length && policy.upper && policy.digit && policy.special;
  const matchOk = form.password && form.password === form.password_confirm;

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_AVATAR_BYTES) { toast.error(t("auth.photo_too_large")); return; }
    if (!file.type.startsWith("image/")) { toast.error(t("auth.photo_invalid_type")); return; }
    const dataUrl = await readFileAsDataURL(file);
    setForm((f) => ({ ...f, avatar_url: dataUrl }));
    setAvatarPreview(dataUrl);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!policyOk) { toast.error(t("auth.password_policy_error")); return; }
    if (!matchOk) { toast.error(t("auth.password_mismatch")); return; }
    setLoading(true);
    try {
      const data = await register({ ...form, role });
      if (data.role_added) {
        toast.success(t("auth.role_added_success"));
        navigate(role === "instructor" ? "/instructor" : "/");
        return;
      }
      if (data.requires_verification) {
        setSentTo(form.email);
        return;
      }
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (sentTo) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-[#050505]" data-testid="register-verify-screen">
        <div className="w-full max-w-lg text-center">
          <Link to="/" className="flex items-center justify-center gap-2 mb-10">
            <Lightning size={28} weight="fill" className="text-[#E30613]" />
            <span className="font-display text-2xl uppercase font-black">FitAgenda <span className="text-[#E30613]">CH</span></span>
          </Link>
          <div className="border border-white/10 bg-[#0A0A0A] p-10">
            <EnvelopeSimple size={56} weight="duotone" className="text-[#E30613] mx-auto" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold mt-6">{t("auth.verify_sent_tag")}</div>
            <h1 className="font-display text-3xl uppercase font-black mt-2">{t("auth.verify_sent_title")}</h1>
            <p className="text-sm text-zinc-300 mt-4 leading-relaxed">{t("auth.verify_sent_body", { email: sentTo })}</p>
            <p className="text-xs text-zinc-500 mt-4 leading-relaxed">{t("auth.verify_sent_hint")}</p>
            <Link to="/login" className="inline-block mt-6 text-xs uppercase tracking-widest text-[#E30613] hover:text-white font-bold" data-testid="verify-sent-login-link">
              {t("auth.login_link")} →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-[#050505]" data-testid="register-page">
      <div className="w-full max-w-lg">
        <div className="flex justify-end mb-4"><LanguageSwitcher /></div>
        <Link to="/" className="flex items-center justify-center gap-2 mb-10">
          <Lightning size={28} weight="fill" className="text-[#E30613]" />
          <span className="font-display text-2xl uppercase font-black">FitAgenda <span className="text-[#E30613]">CH</span></span>
        </Link>

        <div className="border border-white/10 bg-[#0A0A0A] p-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("auth.register_tag")}</div>
          <h1 className="font-display text-4xl uppercase font-black mt-2">{t("auth.register_title")}</h1>

          <div className="mt-6 grid grid-cols-2 gap-2">
            {[
              { id: "client", label: t("auth.role_client"), icon: User, desc: t("auth.role_client_desc") },
              { id: "instructor", label: t("auth.role_instructor"), icon: Barbell, desc: t("auth.role_instructor_desc") }
            ].map((r) => (
              <button key={r.id} type="button" onClick={() => setRole(r.id)}
                className={`p-4 text-left border transition-colors ${role === r.id ? "border-[#E30613] bg-[#E30613]/5" : "border-white/15 hover:border-white/40"}`}
                data-testid={`register-role-${r.id}`}>
                <r.icon size={20} className={role === r.id ? "text-[#E30613]" : "text-zinc-500"} weight="duotone" />
                <div className={`mt-2 text-xs uppercase tracking-widest font-bold ${role === r.id ? "text-white" : "text-zinc-400"}`}>{r.label}</div>
                <div className="text-[10px] text-zinc-500 mt-1">{r.desc}</div>
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.name")}</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="register-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.email")}</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="register-email-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.password")}</Label>
              <Input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="register-password-input" />
              {form.password && (
                <ul className="text-[10px] space-y-1 mt-2">
                  {[
                    { ok: policy.length, txt: t("auth.policy_length") },
                    { ok: policy.upper, txt: t("auth.policy_upper") },
                    { ok: policy.digit, txt: t("auth.policy_digit") },
                    { ok: policy.special, txt: t("auth.policy_special") },
                  ].map((p, i) => (
                    <li key={i} className={`flex items-center gap-2 ${p.ok ? "text-[#00FF66]" : "text-zinc-500"}`} data-testid={`policy-item-${i}`}>
                      {p.ok ? <Check size={12} weight="bold" /> : <X size={12} weight="bold" />} {p.txt}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.password_confirm")}</Label>
              <Input type="password" required value={form.password_confirm} onChange={(e) => setForm({ ...form, password_confirm: e.target.value })}
                className={`bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613] ${form.password_confirm && !matchOk ? "border-red-500" : ""}`}
                data-testid="register-password-confirm-input" />
              {form.password_confirm && !matchOk && (
                <div className="text-[10px] text-red-400 flex items-center gap-1"><X size={12} weight="bold" /> {t("auth.password_mismatch")}</div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.phone")}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("auth.phone_placeholder")}
                className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="register-phone-input" />
            </div>
            {role === "instructor" && (
              <>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.date_of_birth")}</Label>
                  <Input type="date" required value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                    className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="register-dob-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.address")}</Label>
                  <Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder={t("auth.address_placeholder")}
                    className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="register-address-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.photo")}</Label>
                  <div className="flex items-center gap-4">
                    <label htmlFor="avatar-upload" className="cursor-pointer inline-block bg-white/5 hover:bg-white/10 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold text-white">
                      {form.avatar_url ? t("auth.photo_change") : t("auth.photo_choose")}
                    </label>
                    <input id="avatar-upload" type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="register-photo-input" />
                    {avatarPreview && (
                      <img src={avatarPreview} alt="preview" className="w-16 h-16 object-cover border border-white/20" />
                    )}
                  </div>
                  <div className="text-[10px] text-zinc-500">{t("auth.photo_hint")}</div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.bio")}</Label>
                  <Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                    placeholder={t("auth.bio_placeholder")} required
                    className="bg-black border-white/20 rounded-none focus-visible:ring-[#E30613] focus-visible:border-[#E30613]" data-testid="register-bio-input" />
                </div>
                <label className="flex items-start gap-3 cursor-pointer p-3 border border-white/10 bg-black" data-testid="register-attestation-label">
                  <input type="checkbox" required checked={form.attestation_accepted} onChange={(e) => setForm({ ...form, attestation_accepted: e.target.checked })}
                    className="mt-1 shrink-0" data-testid="register-attestation" />
                  <span className="text-xs text-zinc-300 leading-relaxed">{t("auth.attestation")}</span>
                </label>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{t("auth.instructor_note")}</div>
              </>
            )}

            <Button type="submit" disabled={loading || !policyOk || !matchOk}
              className="w-full bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none h-12 btn-tactical disabled:opacity-50"
              data-testid="register-submit-button">
              {loading ? t("auth.registering") : t("auth.register_action")}
            </Button>
          </form>

          <div className="mt-6 text-xs text-zinc-500 text-center">
            {t("auth.have_account")}{" "}
            <Link to="/login" className="text-[#E30613] hover:text-white font-bold uppercase tracking-wider" data-testid="register-login-link">
              {t("auth.login_link")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
